import type { SoundsSnapshot } from '../audio/sound-pack';
// Local, explicitly opted-in device-test evidence. No transport or stored opt-in.
export type DeviceOperation = 'resume' | 'suspend' | 'decode' | 'source';
export type DeviceEvent =
  | 'visibilitychange'
  | 'pageshow'
  | 'pagehide'
  | 'freeze'
  | 'resume'
  | 'focusin'
  | 'focusout'
  | 'resize'
  | 'scroll'
  | 'error'
  | 'unhandledrejection'
  | 'online'
  | 'offline'
  | 'pointerdown'
  | 'pointerup'
  | 'pointercancel'
  | 'click'
  | 'keydown'
  | 'audio-statechange'
  | 'audio-recreate'
  | `${'resume' | 'suspend'}-${'requested' | 'resolved' | 'rejected'}`;
/** Audio Session API (Safari 17+); iOS mutes Web Audio under `ambient` when the ringer is silenced. */
type AudioSessionSnapshot = { type: string; state: string } | null;
export type OperationCounts = {
  pending: number;
  resolved: number;
  rejected: number;
  lastDurationMs: number;
  maxDurationMs: number;
};
export type DeviceState = {
  theme: string;
  preferences: { effects: boolean; music: boolean; ambience: boolean; animations: boolean };
  audio: SoundsSnapshot;
};
export type DeviceDiagnosticsSnapshot = DeviceState & {
  version: 1;
  asset: string;
  events: {
    sequence: number;
    at: number;
    category: DeviceEvent;
    audioState?: 'running' | 'suspended' | 'interrupted' | 'closed' | 'unknown';
  }[];
  dropped: number;
  counters: Record<DeviceOperation, OperationCounts>;
  browser: {
    visibility: 'visible' | 'hidden';
    audioSession: AudioSessionSnapshot;
    online: boolean;
    focused: boolean;
    focus: 'input' | 'textarea' | 'button' | 'select' | 'other' | 'none';
    viewport: { width: number; height: number; scale: number; offsetTop: number; offsetLeft: number };
  };
};
declare global {
  interface Window {
    __spellieDeviceDiagnostics?: { version: 1; snapshot(): DeviceDiagnosticsSnapshot };
  }
}

export class DeviceDiagnostics {
  private events: DeviceDiagnosticsSnapshot['events'] = [];
  private sequence = 0;
  private dropped = 0;
  readonly counters: Record<DeviceOperation, OperationCounts> = {
    resume: { pending: 0, resolved: 0, rejected: 0, lastDurationMs: 0, maxDurationMs: 0 },
    suspend: { pending: 0, resolved: 0, rejected: 0, lastDurationMs: 0, maxDurationMs: 0 },
    decode: { pending: 0, resolved: 0, rejected: 0, lastDurationMs: 0, maxDurationMs: 0 },
    source: { pending: 0, resolved: 0, rejected: 0, lastDurationMs: 0, maxDurationMs: 0 },
  };
  record(
    category: DeviceEvent,
    audioState?: DeviceDiagnosticsSnapshot['events'][number]['audioState'],
  ): void {
    if (this.events.length === 256) {
      this.events.shift();
      this.dropped++;
    }
    this.events.push({
      sequence: ++this.sequence,
      at: performance.now(),
      category,
      ...(audioState ? { audioState } : {}),
    });
  }
  begin(operation: DeviceOperation): (ok: boolean) => void {
    const counts = this.counters[operation];
    const started = performance.now();
    if (operation === 'resume' || operation === 'suspend') this.record(`${operation}-requested`);
    counts.pending++;
    let settled = false;
    return (ok) => {
      if (settled) return;
      settled = true;
      counts.pending--;
      counts[ok ? 'resolved' : 'rejected']++;
      counts.lastDurationMs = performance.now() - started;
      counts.maxDurationMs = Math.max(counts.maxDurationMs, counts.lastDurationMs);
      if (operation === 'resume' || operation === 'suspend')
        this.record(`${operation}-${ok ? 'resolved' : 'rejected'}`);
    };
  }
  evidence() {
    return {
      events: this.events.map((event) => ({ ...event })),
      dropped: this.dropped,
      counters: Object.fromEntries(
        Object.entries(this.counters).map(([key, value]) => [key, { ...value }]),
      ) as Record<DeviceOperation, OperationCounts>,
    };
  }
}

export let deviceDiagnostics: DeviceDiagnostics | undefined;

/** No listeners, observers, timers, or snapshot providers run when disabled. */
export function installDeviceDiagnostics(readState: () => DeviceState, host: Window = window): () => void {
  if (
    new URLSearchParams(host.location.search).get('deviceDiagnostics') !== '1' ||
    host.__spellieDeviceDiagnostics
  )
    return () => {};
  const diagnostics = new DeviceDiagnostics();
  deviceDiagnostics = diagnostics;
  const doc = host.document;
  const removers: (() => void)[] = [];
  const listen = (target: EventTarget, category: DeviceEvent) => {
    const handler = () => diagnostics.record(category);
    target.addEventListener(category, handler);
    removers.push(() => target.removeEventListener(category, handler));
  };
  for (const event of [
    'visibilitychange',
    'freeze',
    'resume',
    'focusin',
    'focusout',
    'pointerdown',
    'pointerup',
    'pointercancel',
    'click',
    'keydown',
  ] as const)
    listen(doc, event);
  for (const event of [
    'pageshow',
    'pagehide',
    'resize',
    'error',
    'unhandledrejection',
    'online',
    'offline',
  ] as const)
    listen(host, event);
  if (host.visualViewport)
    for (const event of ['resize', 'scroll'] as const) listen(host.visualViewport, event);
  const api = {
    version: 1 as const,
    snapshot(): DeviceDiagnosticsSnapshot {
      const tag = doc.activeElement?.tagName.toLowerCase();
      const focus =
        tag === 'input' || tag === 'textarea' || tag === 'button' || tag === 'select'
          ? tag
          : tag
            ? 'other'
            : 'none';
      const viewport = host.visualViewport;
      const audioSession = (host.navigator as Navigator & { audioSession?: { type: string; state: string } })
        ?.audioSession;
      const entry = doc.querySelector?.('script[type="module"][src]')?.getAttribute('src')?.split('/').at(-1);
      return {
        version: 1,
        // Content-hashed production entry identifies the served frontend, without exposing URLs.
        asset: entry && /^index-[a-zA-Z0-9_-]{1,64}\.js$/.test(entry) ? entry : 'unknown',
        ...readState(),
        ...diagnostics.evidence(),
        browser: {
          visibility: doc.visibilityState === 'hidden' ? 'hidden' : 'visible',
          audioSession: audioSession
            ? { type: String(audioSession.type), state: String(audioSession.state) }
            : null,
          online: host.navigator?.onLine ?? true,
          focused: doc.hasFocus(),
          focus,
          viewport: {
            width: viewport?.width ?? host.innerWidth,
            height: viewport?.height ?? host.innerHeight,
            scale: viewport?.scale ?? 1,
            offsetTop: viewport?.offsetTop ?? 0,
            offsetLeft: viewport?.offsetLeft ?? 0,
          },
        },
      };
    },
  };
  host.__spellieDeviceDiagnostics = api;
  return () => {
    removers.forEach((remove) => remove());
    if (host.__spellieDeviceDiagnostics === api) delete host.__spellieDeviceDiagnostics;
    if (deviceDiagnostics === diagnostics) deviceDiagnostics = undefined;
  };
}
